import React, { useEffect, useState } from "react";
import { Card, Row, Col, Select, DatePicker, Button, Table, message, Space } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import API from "../../../api";
import dayjs from "dayjs";
import * as XLSX from 'xlsx';

const { Option } = Select;
const { RangePicker } = DatePicker;

const GstReport = () => {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(null);
  const [loading, setLoading] = useState(false);
  const [outputGST, setOutputGST] = useState(null);
  const [inputGST, setInputGST] = useState(null);

  // Fetch stores for the dropdown
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await API.get("/stores");
        setStores(response?.data || []);
      } catch (error) {
        message.error("Failed to fetch stores.");
      }
    };
    fetchStores();
  }, []);

  // Generate financial years
  const generateFinancialYears = () => {
    const currentDate = new Date();
    const financialYearStart = new Date(currentDate.getFullYear(), 3, 1); // April 1st
    let currentFinancialYearStart;
    if (currentDate < financialYearStart) {
      currentFinancialYearStart = currentDate.getFullYear() - 1;
    } else {
      currentFinancialYearStart = currentDate.getFullYear();
    }
    const financialYears = [];
    let startYear = 2024;
    let endYear = startYear + 1;
    while (startYear <= currentFinancialYearStart) {
      financialYears.push(`${startYear}-${endYear}`);
      startYear++;
      endYear++;
    }
    return financialYears;
  };

  // Fetch GST report
  const fetchGstReport = async () => {
    setLoading(true);
    try {
      const params = {
        ...(selectedStore && { storeId: selectedStore }),
        ...(selectedDates.length === 2 && {
          fromDate: selectedDates[0].format("YYYY-MM-DD"),
          toDate: selectedDates[1].format("YYYY-MM-DD"),
        }),
        ...(selectedFinancialYear && { financialYear: selectedFinancialYear }),
      };
      const response = await API.get("/accounts/gst-report", { params });
      console.log("GST Report Response:", response.data); // Debug log
      
      if (response.data.success) {
        setOutputGST(response.data.data.outputGST);
        setInputGST(response.data.data.inputGST);
      } else {
        message.error("Failed to fetch GST report.");
        setOutputGST(null);
        setInputGST(null);
      }
    } catch (error) {
      console.error("GST Report Error:", error);
      message.error("Failed to fetch GST report.");
      setOutputGST(null);
      setInputGST(null);
    } finally {
      setLoading(false);
    }
  };

  // Download Output GST (Sales) as Excel
  const downloadOutputGST = () => {
    if (!outputGST || !outputGST.sales || outputGST.sales.length === 0) {
      message.warning("No data available to download");
      return;
    }

    try {
      // Prepare data for Excel
      const excelData = outputGST.sales.map((sale, index) => ({
        'Sr. No': index + 1,
        'Invoice No': sale.invoiceId || 'N/A',
        'Date': sale.date ? dayjs(sale.date).format('DD-MM-YYYY') : 'N/A',
        'Customer Name': sale.customerName || 'N/A',
        'Customer GST': sale.customerGST || 'N/A',
        'Taxable Value (₹)': sale.taxableValue ? parseFloat(sale.taxableValue).toFixed(2) : '0.00',
        'CGST (₹)': sale.cgst ? parseFloat(sale.cgst).toFixed(2) : '0.00',
        'SGST (₹)': sale.sgst ? parseFloat(sale.sgst).toFixed(2) : '0.00',
        'IGST (₹)': sale.igst ? parseFloat(sale.igst).toFixed(2) : '0.00',
        'Total GST (₹)': sale.totalGST ? parseFloat(sale.totalGST).toFixed(2) : '0.00',
      }));

      // Add summary row
      excelData.push({
        'Sr. No': '',
        'Invoice No': '',
        'Date': '',
        'Customer Name': '',
        'Customer GST': '',
        'Taxable Value (₹)': `Total: ₹${outputGST.totalTaxableValue?.toFixed(2) || '0.00'}`,
        'CGST (₹)': `Total: ₹${outputGST.totalCGST?.toFixed(2) || '0.00'}`,
        'SGST (₹)': `Total: ₹${outputGST.totalSGST?.toFixed(2) || '0.00'}`,
        'IGST (₹)': `Total: ₹${outputGST.totalIGST?.toFixed(2) || '0.00'}`,
        'Total GST (₹)': `Total: ₹${outputGST.totalGST?.toFixed(2) || '0.00'}`,
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [
        { wch: 8 },   // Sr. No
        { wch: 15 },  // Invoice No
        { wch: 12 },  // Date
        { wch: 25 },  // Customer Name
        { wch: 20 },  // Customer GST
        { wch: 15 },  // Taxable Value
        { wch: 12 },  // CGST
        { wch: 12 },  // SGST
        { wch: 12 },  // IGST
        { wch: 15 },  // Total GST
      ];
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Output GST (Sales)');

      // Generate filename
      const storeName = stores.find(s => s._id === selectedStore)?.name || 'All Stores';
      const dateRange = selectedDates.length === 2 
        ? `${selectedDates[0].format('DD-MM-YYYY')}_to_${selectedDates[1].format('DD-MM-YYYY')}`
        : 'All Dates';
      const filename = `Output_GST_${storeName}_${dateRange}_${dayjs().format('DD-MM-YYYY')}.xlsx`;

      // Download file
      XLSX.writeFile(wb, filename);
      message.success('Output GST report downloaded successfully!');
    } catch (error) {
      console.error('Error downloading Output GST:', error);
      message.error('Failed to download Output GST report');
    }
  };

  // Download Input GST (Purchases) as Excel
  const downloadInputGST = () => {
    if (!inputGST || !inputGST.purchases || inputGST.purchases.length === 0) {
      message.warning("No data available to download");
      return;
    }

    try {
      // Prepare data for Excel
      const excelData = inputGST.purchases.map((purchase, index) => ({
        'Sr. No': index + 1,
        'Invoice No': purchase.invoiceId || 'N/A',
        'Date': purchase.date ? dayjs(purchase.date).format('DD-MM-YYYY') : 'N/A',
        'Supplier Name': purchase.supplierName || 'N/A',
        'Supplier GST': purchase.supplierGST || 'N/A',
        'Taxable Value (₹)': purchase.taxableValue ? parseFloat(purchase.taxableValue).toFixed(2) : '0.00',
        'CGST (₹)': purchase.cgst ? parseFloat(purchase.cgst).toFixed(2) : '0.00',
        'SGST (₹)': purchase.sgst ? parseFloat(purchase.sgst).toFixed(2) : '0.00',
        'IGST (₹)': purchase.igst ? parseFloat(purchase.igst).toFixed(2) : '0.00',
        'Total GST (₹)': purchase.totalGST ? parseFloat(purchase.totalGST).toFixed(2) : '0.00',
      }));

      // Add summary row
      excelData.push({
        'Sr. No': '',
        'Invoice No': '',
        'Date': '',
        'Supplier Name': '',
        'Supplier GST': '',
        'Taxable Value (₹)': `Total: ₹${inputGST.totalTaxableValue?.toFixed(2) || '0.00'}`,
        'CGST (₹)': `Total: ₹${inputGST.totalCGST?.toFixed(2) || '0.00'}`,
        'SGST (₹)': `Total: ₹${inputGST.totalSGST?.toFixed(2) || '0.00'}`,
        'IGST (₹)': `Total: ₹${inputGST.totalIGST?.toFixed(2) || '0.00'}`,
        'Total GST (₹)': `Total: ₹${inputGST.totalGST?.toFixed(2) || '0.00'}`,
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [
        { wch: 8 },   // Sr. No
        { wch: 15 },  // Invoice No
        { wch: 12 },  // Date
        { wch: 25 },  // Supplier Name
        { wch: 20 },  // Supplier GST
        { wch: 15 },  // Taxable Value
        { wch: 12 },  // CGST
        { wch: 12 },  // SGST
        { wch: 12 },  // IGST
        { wch: 15 },  // Total GST
      ];
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Input GST (Purchases)');

      // Generate filename
      const storeName = stores.find(s => s._id === selectedStore)?.name || 'All Stores';
      const dateRange = selectedDates.length === 2 
        ? `${selectedDates[0].format('DD-MM-YYYY')}_to_${selectedDates[1].format('DD-MM-YYYY')}`
        : 'All Dates';
      const filename = `Input_GST_${storeName}_${dateRange}_${dayjs().format('DD-MM-YYYY')}.xlsx`;

      // Download file
      XLSX.writeFile(wb, filename);
      message.success('Input GST report downloaded successfully!');
    } catch (error) {
      console.error('Error downloading Input GST:', error);
      message.error('Failed to download Input GST report');
    }
  };

  // Columns for Output GST (Sales)
  const outputColumns = [
    { title: "Invoice No", dataIndex: "invoiceId", key: "invoiceId" },
    { title: "Date", dataIndex: "date", key: "date", render: (date) => dayjs(date).format("DD-MM-YYYY") },
    { title: "Customer Name", dataIndex: "customerName", key: "customerName" },
    { title: "Customer GST", dataIndex: "customerGST", key: "customerGST" },
    { title: "Taxable Value", dataIndex: "taxableValue", key: "taxableValue", render: (v) => `₹${v.toFixed(2)}` },
    { title: "CGST", dataIndex: "cgst", key: "cgst", render: (v) => `₹${v.toFixed(2)}` },
    { title: "SGST", dataIndex: "sgst", key: "sgst", render: (v) => `₹${v.toFixed(2)}` },
    { title: "IGST", dataIndex: "igst", key: "igst", render: (v) => `₹${v.toFixed(2)}` },
    { title: "Total GST", dataIndex: "totalGST", key: "totalGST", render: (v) => `₹${v.toFixed(2)}` },
  ];

  // Columns for Input GST (Purchases)
  const inputColumns = [
    { title: "Invoice No", dataIndex: "invoiceId", key: "invoiceId" },
    { title: "Date", dataIndex: "date", key: "date", render: (date) => dayjs(date).format("DD-MM-YYYY") },
    { title: "Supplier Name", dataIndex: "supplierName", key: "supplierName" },
    { title: "Supplier GST", dataIndex: "supplierGST", key: "supplierGST" },
    { title: "Taxable Value", dataIndex: "taxableValue", key: "taxableValue", render: (v) => `₹${v.toFixed(2)}` },
    { title: "CGST", dataIndex: "cgst", key: "cgst", render: (v) => `₹${v.toFixed(2)}` },
    { title: "SGST", dataIndex: "sgst", key: "sgst", render: (v) => `₹${v.toFixed(2)}` },
    { title: "IGST", dataIndex: "igst", key: "igst", render: (v) => `₹${v.toFixed(2)}` },
    { title: "Total GST", dataIndex: "totalGST", key: "totalGST", render: (v) => `₹${v.toFixed(2)}` },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={8} md={6} lg={5}>
            <Select
              placeholder="Select Store"
              value={selectedStore}
              onChange={setSelectedStore}
              allowClear
              style={{ width: "100%" }}
            >
              {stores.map((store) => (
                <Option key={store._id} value={store._id}>
                  {store.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={10} md={8} lg={7}>
            <RangePicker
              value={selectedDates}
              onChange={setSelectedDates}
              style={{ width: "100%" }}
              format="DD-MM-YYYY"
              allowClear
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={5}>
            <Select
              placeholder="Select Financial Year"
              value={selectedFinancialYear}
              onChange={setSelectedFinancialYear}
              allowClear
              style={{ width: "100%" }}
            >
              {generateFinancialYears().map((year) => (
                <Option key={year} value={year}>
                  {year}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={6} md={4} lg={3}>
            <Space>
              <Button type="primary" onClick={fetchGstReport} loading={loading}>
                Get Report
              </Button>
              <Button
                onClick={() => {
                  setSelectedStore(null);
                  setSelectedDates([]);
                  setSelectedFinancialYear(null);
                  setOutputGST(null);
                  setInputGST(null);
                }}
              >
                Reset
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Output GST Table */}
      <Card
        title="Output GST (Sales)"
        style={{ marginBottom: 24 }}
        extra={
          <Space>
            {outputGST && (
              <>
                <span>Taxable: <b>₹{outputGST.totalTaxableValue?.toFixed(2) || 0}</b></span>
                <span>CGST: <b>₹{outputGST.totalCGST?.toFixed(2) || 0}</b></span>
                <span>SGST: <b>₹{outputGST.totalSGST?.toFixed(2) || 0}</b></span>
                <span>IGST: <b>₹{outputGST.totalIGST?.toFixed(2) || 0}</b></span>
                <span>Total GST: <b>₹{outputGST.totalGST?.toFixed(2) || 0}</b></span>
                <Button 
                  type="primary" 
                  icon={<DownloadOutlined />} 
                  onClick={downloadOutputGST}
                  disabled={!outputGST.sales || outputGST.sales.length === 0}
                >
                  Download Excel
                </Button>
              </>
            )}
          </Space>
        }
      >
        {outputGST ? (
          <Table
            columns={outputColumns}
            dataSource={outputGST.sales || []}
            rowKey="invoiceId"
            loading={loading}
            pagination={{ pageSize: 10 }}
            scroll={{ x: true }}
            locale={{
              emptyText: "No sales data found for the selected filters"
            }}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            {loading ? 'Loading...' : 'No data available. Please select filters and click "Get Report".'}
          </div>
        )}
      </Card>

      {/* Input GST Table */}
      <Card
        title="Input GST (Purchases)"
        style={{ marginBottom: 24 }}
        extra={
          <Space>
            {inputGST && (
              <>
                <span>Taxable: <b>₹{inputGST.totalTaxableValue?.toFixed(2) || 0}</b></span>
                <span>CGST: <b>₹{inputGST.totalCGST?.toFixed(2) || 0}</b></span>
                <span>SGST: <b>₹{inputGST.totalSGST?.toFixed(2) || 0}</b></span>
                <span>IGST: <b>₹{inputGST.totalIGST?.toFixed(2) || 0}</b></span>
                <span>Total GST: <b>₹{inputGST.totalGST?.toFixed(2) || 0}</b></span>
                <Button 
                  type="primary" 
                  icon={<DownloadOutlined />} 
                  onClick={downloadInputGST}
                  disabled={!inputGST.purchases || inputGST.purchases.length === 0}
                >
                  Download Excel
                </Button>
              </>
            )}
          </Space>
        }
      >
        {inputGST ? (
          <Table
            columns={inputColumns}
            dataSource={inputGST.purchases || []}
            rowKey="invoiceId"
            loading={loading}
            pagination={{ pageSize: 10 }}
            scroll={{ x: true }}
            locale={{
              emptyText: "No purchase data found for the selected filters"
            }}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            {loading ? 'Loading...' : 'No data available. Please select filters and click "Get Report".'}
          </div>
        )}
      </Card>

      {/* Net GST Summary */}
      {outputGST && inputGST && (
        <Card title="Net GST Summary" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={6}>
              <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#f6ffed', borderRadius: '8px' }}>
                <div style={{ fontSize: '14px', color: '#666' }}>Net CGST</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}>
                  ₹{((outputGST.totalCGST || 0) - (inputGST.totalCGST || 0)).toFixed(2)}
                </div>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#fff7e6', borderRadius: '8px' }}>
                <div style={{ fontSize: '14px', color: '#666' }}>Net SGST</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#fa8c16' }}>
                  ₹{((outputGST.totalSGST || 0) - (inputGST.totalSGST || 0)).toFixed(2)}
                </div>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#e6f7ff', borderRadius: '8px' }}>
                <div style={{ fontSize: '14px', color: '#666' }}>Net IGST</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}>
                  ₹{((outputGST.totalIGST || 0) - (inputGST.totalIGST || 0)).toFixed(2)}
                </div>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#fff2e8', borderRadius: '8px' }}>
                <div style={{ fontSize: '14px', color: '#666' }}>Total Net GST</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#fa541c' }}>
                  ₹{((outputGST.totalGST || 0) - (inputGST.totalGST || 0)).toFixed(2)}
                </div>
              </div>
            </Col>
          </Row>
        </Card>
      )}
    </div>
  );
};

export default GstReport;
